import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  sendGetClientRequest,
  sendPutClientRequest,
  sendPostClientRequest,
  sendAIGetRequest,
  sendAIPostRequest,
  sendDeleteClientRequest,
  sendGetClientRequestRaw,
  sendPostRequest,
} from "../../../api/helper";
import { serialize } from "../../../utils";

export const postEnterpriseGiftStartOne = createAsyncThunk(
  "enterpriseCompany/postEnterpriseGiftStartOne",
  async (parameters) => {
    const postObj = {
      enterprise_id: parameters.enterprise_id,
      points: parameters.points,
      remark: parameters.remark || "",
    };
    const response = await sendPostClientRequest(
      `/api/center/shop/enterprise/give_gift/ai_agent_token_point`,
      postObj
    );
    return response;
  }
);

export const getEnterpriseAssets = createAsyncThunk(
  "enterpriseCompany/getEnterpriseAssets",
  async ({ enterpriseId }) => {
    const response = await sendAIGetRequest(
      `/api/system-admin/enterprise-assets?enterpriseId=${enterpriseId}`
    );
    return response;
  }
);

export const postGiftStartOne = createAsyncThunk(
  "enterpriseCompany/postGiftStartOne",
  async (parameters) => {
    let postObj = {};
    postObj = {
      user_id: parameters?.user_id,
      points: parameters?.points,
      remark: "Manual grant",
    };
    if (parameters?.start_date) {
      postObj.start_at = parameters?.start_date;
    }
    if (parameters?.end_date) {
      postObj.end_at = parameters?.end_date;
    }
    const response = await sendPostClientRequest(
      `/api/center/shop/give_gift/ai_agent_token_point`,
      postObj
    );
    return response;
  }
);

export const getHistoryStartOne = createAsyncThunk(
  "enterpriseCompany/getHistoryStartOne",
  async (parameters) => {
    const queryObj = {};
    if (parameters) {
      if (parameters.user_id) queryObj.user_id = parameters.user_id;
      if (parameters.limit) queryObj.limit = parameters.limit;
      if (parameters.page) queryObj.page = parameters.page;
      if (parameters.search) queryObj.keyword = parameters.search;
    }
    let queryString = "";
    if (JSON.stringify(queryObj) !== "{}")
      queryString = "?" + serialize(queryObj);

    const response = await sendGetClientRequest(
      `/api/center/shop/ai_agent_token_records${queryString}`
    );
    return response?.data;
  }
);

export const upgradePersonalToEnterprise = createAsyncThunk(
  "enterpriseCompany/upgradePersonalToEnterprise",
  async (parameters) => {
    const response = await sendPostRequest(
      `/api/auth/user/enterprise`,
      {
        user_id: parameters.user_id,
        title: parameters.title,
        taxid: parameters.taxid || '',
      }
    );
    return response;
  }
);

// ── 個人帳號「管理方案」（後台開通/試用，純授予不扣款；對應 client-api personal_management）──
// 詳情 shape 與企業一致：{ user_id, user_name, data:[ per-service rows... ] }
export const getPersonalPlanDetail = createAsyncThunk(
  "enterpriseCompany/getPersonalPlanDetail",
  async ({ user_id }) => {
    const response = await sendGetClientRequest(
      `/api/personal_management/${user_id}`
    );
    return response.data;
  }
);

// 切換個人帳號單一服務開通狀態（不扣款）
export const activatePersonalService = createAsyncThunk(
  "enterpriseCompany/activatePersonalService",
  async ({ user_id, service_id, activation_setting }) => {
    const response = await sendPutClientRequest(
      `/api/personal_management/${user_id}/activation_state`,
      { service_id, activation_setting }
    );
    return response;
  }
);

// 個人帳號開通設定（建立/更新帶日期的開通；is_trial true=試用、false=訂閱；皆不扣款）
export const openPersonalTrial = createAsyncThunk(
  "enterpriseCompany/openPersonalTrial",
  async (data) => {
    const response = await sendPostClientRequest(
      `/api/personal_management/${data.user_id}/activate_trial`,
      data
    );
    return response;
  }
);

// 個人帳號訂閱購買紀錄
export const getPersonalSubscriptionHistory = createAsyncThunk(
  "enterpriseCompany/getPersonalSubscriptionHistory",
  async ({ user_id, service_id, page, limit }) => {
    const postObj = {};
    if (service_id) postObj.service_id = service_id;
    if (page) postObj.page = page;
    if (limit) postObj.limit = limit;
    const response = await sendPostClientRequest(
      `/api/personal_management/${user_id}/subscription-history`,
      postObj
    );
    return response.data;
  }
);

// 贈送帳號
export const getGift = createAsyncThunk(
  "enterpriseCompany/getGift",
  async (parameters) => {
    const response = await sendGetClientRequest(
      `/api/enterprise_management/${parameters.enterprise_id}/gift?service_id=${parameters.service_id}`
    );
    return response;
  }
);

export const postGift = createAsyncThunk(
  "enterpriseCompany/postGift",
  async (parameters) => {
    const postObj = {
      service_id: parameters.service_id,
      quantity: parameters.quantity,
      start_date: parameters.start_date,
      expiration_date: parameters.expiration_date,
    };
    const response = await sendPostClientRequest(
      `/api/enterprise_management/${parameters.enterprise_id}/gift`,
      postObj
    );
    return response;
  }
);

export const getEnterpriseCompany = createAsyncThunk(
  "enterpriseCompany/getEnterpriseCompany",
  async (parameters) => {
    const queryObj = {};
    if (parameters) {
      if (parameters.limit) queryObj.limit = parameters.limit;
      if (parameters.currentPage) queryObj.currentPage = parameters.currentPage;
      if (parameters.keyword) queryObj.keyword = parameters.keyword;
    }
    let queryString = "";
    if (JSON.stringify(queryObj) !== "{}")
      queryString = "?" + serialize(queryObj);

    const response = await sendGetClientRequest(
      `/api/enterprise_management/subscribe_services/${parameters.service_id}${queryString}`
    );
    return response.data;
  }
);

export const getEnterpriseCompanyList = createAsyncThunk(
  "enterpriseCompany/getEnterpriseCompanyList",
  async (parameters) => {
    const queryObj = {};
    if (parameters) {
      if (parameters.limit) queryObj.limit = parameters.limit;
      if (parameters.currentPage) queryObj.currentPage = parameters.currentPage;
      if (parameters.keyword) queryObj.keyword = parameters.keyword;
      if (parameters.sort) queryObj.sort = parameters.sort;
      if (parameters.sort_dir) queryObj.sort_dir = parameters.sort_dir;
    }
    let queryString = "";
    if (JSON.stringify(queryObj) !== "{}")
      queryString = "?" + serialize(queryObj);

    const response = await sendGetClientRequest(
      `/api/enterprise_management/${queryString}`
    );
    return response.data;
  }
);

export const getEnterpriseCompanyListDetail = createAsyncThunk(
  "enterpriseCompany/getEnterpriseCompanyListDetail",
  async (parameters) => {
    const response = await sendGetClientRequest(
      `/api/enterprise_management/${parameters?.id}`
    );
    return response.data;
  }
);

// 企業共用配額（儲存空間 + 下載流量）讀取：無設定時後端回預設值。
export const getEnterpriseUsage = createAsyncThunk(
  "enterpriseCompany/getEnterpriseUsage",
  async (parameters) => {
    const response = await sendGetClientRequest(
      `/api/enterprise_management/${parameters?.enterprise_id}/usage`
    );
    return response.data;
  }
);

// 企業共用配額建立／更新（一間企業一組，PUT upsert）。
export const saveEnterpriseUsage = createAsyncThunk(
  "enterpriseCompany/saveEnterpriseUsage",
  async (parameters) => {
    const { enterprise_id, ...body } = parameters;
    const response = await sendPutClientRequest(
      `/api/enterprise_management/${enterprise_id}/usage`,
      body
    );
    return response;
  }
);

// 管理方案「全部儲存」（批次）：一次送多個服務 + 共用配額 → POST .../activate
// body 形如 { services: [...], usage: {...} }；後端在單一 transaction 內原子寫入。
// （相容舊單筆格式：body 帶 service_id 等頂層欄位亦可）
export const activateEnterpriseService = createAsyncThunk(
  "enterpriseCompany/activateEnterpriseService",
  async (parameters) => {
    const { enterprise_id, ...body } = parameters;
    const response = await sendPostClientRequest(
      `/api/enterprise_management/${enterprise_id}/activate`,
      body
    );
    return response;
  }
);

export const enableStartOnePlan = createAsyncThunk(
  "enterpriseCompany/enableStartOnePlan",
  async (parameters) => {
    const response = await sendPostClientRequest(
      `/api/enterprise_management/${parameters.enterprise_id}/enable_start_one_plan`
    );
    return response;
  }
);

export const activateEnterpriseCompany = createAsyncThunk(
  "enterpriseCompany/activateEnterpriseCompany",
  async (parameters) => {
    const postObj = {
      activation_setting: parameters.activation_setting,
      service_id: parameters.service_id,
    };
    const response = await sendPutClientRequest(
      `/api/enterprise_management/${parameters.enterprise_id}/activation_state`,
      postObj
    );
    return response;
  }
);

export const activateUserStartOne = createAsyncThunk(
  "enterpriseCompany/activateUserStartOne",
  async (parameters) => {
    const postObj = {
      activation_state: parameters.activation_state,
    };
    const response = await sendPutClientRequest(
      `/api/users/${parameters.user_id}/start-one-activation`,
      postObj
    );
    return response;
  }
);

export const getSubscriptionHistory = createAsyncThunk(
  "enterpriseCompany/getSubscriptionHistory",
  async (parameters) => {
    const postObj = {
      service_id: parameters.service_id,
      page: parameters.page,
      limit: parameters.limit,
    };
    const response = await sendPostClientRequest(
      `/api/enterprise_management/${parameters.enterprise_id}/subscription-history`,
      postObj
    );
    return response.data;
  }
);

export const getEnterpriseOrders = createAsyncThunk(
  "enterpriseCompany/getEnterpriseOrders",
  async (parameters) => {
    const queryObj = {};
    if (parameters) {
      if (parameters.page) queryObj.page = parameters.page;
      if (parameters.limit) queryObj.limit = parameters.limit;
    }
    let queryString = "";
    if (JSON.stringify(queryObj) !== "{}")
      queryString = "?" + serialize(queryObj);

    const response = await sendGetClientRequest(
      `/api/center/shop/enterprise/${parameters.enterprise_id}/orders${queryString}`
    );
    return response.data;
  }
);

export const resendInvoiceMail = createAsyncThunk(
  "enterpriseCompany/resendInvoiceMail",
  async ({ serviceId, orderUuid, transactionLogId, taxId }) => {
    const isShopOrder = serviceId === 8;
    const path = isShopOrder
      ? `/api/center/shop/orders/${orderUuid}/resend-invoice`
      : `/api/center/subscription-orders/${orderUuid}/resend-invoice`;
    const query = transactionLogId ? `?transaction_log_id=${transactionLogId}` : "";
    const body = taxId ? { tax_id: taxId } : {};
    const response = await sendPostClientRequest(path + query, body);
    return response.data;
  }
);

export const syncInvoiceFromEcpay = createAsyncThunk(
  "enterpriseCompany/syncInvoiceFromEcpay",
  async ({ serviceId, orderUuid }) => {
    const isShopOrder = serviceId === 8;
    const path = isShopOrder
      ? `/api/center/shop/orders/${orderUuid}/sync-invoice`
      : `/api/center/subscription-orders/${orderUuid}/sync-invoice`;
    const response = await sendPostClientRequest(path);
    return response.data;
  }
);

export const openTrialPlan = createAsyncThunk(
  "enterpriseCompany/openTrialPlan",
  async (data) => {
    const response = await sendPostClientRequest(
      `/api/enterprise_management/${data.enterprise_id}/activate_trial`,
      data
    );
    return response;
  }
);

export const removeCompany = createAsyncThunk(
  "enterpriseCompany/removeCompany",
  async ({ enterprise_id }) => {
    const response = await sendPostClientRequest(
      `/api/enterprise_management/${enterprise_id}/delete`
    );
    return response;
  }
);

export const getPersonalLists = createAsyncThunk(
  "enterpriseCompany/getPersonalLists",
  async (parameters) => {
    const queryObj = {};
    if (parameters) {
      if (parameters.rows) queryObj.rows = parameters.rows;
      if (parameters.page) queryObj.page = parameters.page;
      if (parameters.search) queryObj.search = parameters.search;
      if (parameters.invoice_status) queryObj.invoice_status = parameters.invoice_status;
      if (parameters.sort) queryObj.sort = parameters.sort;
      if (parameters.sort_method) queryObj.sort_method = parameters.sort_method;
    }
    let queryString = "";
    if (JSON.stringify(queryObj) !== "{}")
      queryString = "?" + serialize(queryObj);

    const response = await sendGetClientRequest(`/api/users${queryString}`);
    return response.data;
  }
);

export const getUserLists = createAsyncThunk(
  "enterpriseCompany/getUserLists",
  async (parameters) => {
    const queryObj = {};
    if (parameters) {
      if (parameters.limit) queryObj.limit = parameters.limit;
      if (parameters.page) queryObj.page = parameters.page;
      // if (parameters.search) queryObj.search = parameters.search;
    }
    let queryString = "";
    if (JSON.stringify(queryObj) !== "{}")
      queryString = "?" + serialize(queryObj);

    const response = await sendAIGetRequest(`/api/admin/users${queryString}`);
    return response;
  }
);

export const postGiftCredits = createAsyncThunk(
  "enterpriseCompany/postGiftCredits",
  async (parameters) => {
    const postObj = {
      email: parameters.email,
      amount: parameters.amount,
      startDate: parameters.startDate,
      endDate: parameters.endDate,
    };
    const response = await sendAIPostRequest(`/api/admin/gift`, postObj);
    return response;
  }
);

export const postCancelGiftAI = createAsyncThunk(
  "enterpriseCompany/postCancelGiftAI",
  async (parameters) => {
    const postObj = {
      email: parameters.email,
      giftId: parameters.giftId,
      cancelReason: parameters.cancelReason,
    };
    const response = await sendAIPostRequest(`/api/admin/gift/cancel`, postObj);
    return response;
  }
);

export const postCancelGift = createAsyncThunk(
  "enterpriseCompany/postCancelGift",
  async (parameters) => {
    const postObj = {
      user_id: parameters.userId,
      order_id: parameters.orderId,
    };
    const response = await sendPostClientRequest(`/api/center/shop/give_gift/ai_agent_token_point/cancel`, postObj);
    return response;
  }
);

export const updateInvoiceStatus = createAsyncThunk(
  "enterpriseCompany/updateInvoiceStatus",
  async (parameters) => {
    const postObj = {
      taxid: parameters.taxid,
      invoiced_at: parameters.invoiced_at,
    };
    const response = await sendPutClientRequest(
      `/api/center/shop/orders/${parameters.orderId}`,
      postObj
    );
    return response;
  }
);

export const postManualInvoice = createAsyncThunk(
  "enterpriseCompany/postManualInvoice",
  async (parameters) => {
    const formData = new FormData();
    formData.append("invoice", parameters.file);
    const response = await sendPostClientRequest(
      `/api/center/shop/orders/${parameters.orderId}/manual_invoice`,
      formData,
      true
    );
    return response;
  }
);

export const deleteManualInvoice = createAsyncThunk(
  "enterpriseCompany/deleteManualInvoice",
  async (parameters) => {
    const response = await sendDeleteClientRequest(
      `/api/center/shop/orders/${parameters.orderId}/manual_invoice`
    );
    return response;
  }
);

export const getManualInvoice = createAsyncThunk(
  "enterpriseCompany/getManualInvoice",
  async (parameters) => {
    const response = await sendGetClientRequestRaw(
      `/api/center/shop/orders/${parameters.orderId}/manual_invoice`,
      "blob"
    );
    return {
      data: response.data,
      headers: response.headers,
    };
  }
);

const enterpriseCompanySlice = createSlice({
  name: "enterpriseCompany",
  initialState: {
    business: [],
    isSuccess: false,
    loading: false,
    error: null,
    permission_error: false,
    history: "",
    gift: "",
    isGiftSuccess: false,
    isGiftPointsSuccess: false,
    order_history: [],
    users_topup: "",
    invoice_detail: "",
    enterpriseAssets: {},
  },
  reducers: {
    removeSuccess: (state) => {
      state.isSuccess = null;
      state.isGiftSuccess = false;
      state.isGiftPointsSuccess = false;
      state.error = null;
    },
  },
  extraReducers: {
    // GET GIFT HISTORY START ONE
    [getHistoryStartOne.pending]: () => {},
    [getHistoryStartOne.fulfilled]: (state, { payload }) => {
      if (payload) state.order_history = payload;
    },
    [getHistoryStartOne.rejected]: () => {},

    // GET ENTERPRISE ASSETS
    [getEnterpriseAssets.pending]: () => {},
    [getEnterpriseAssets.fulfilled]: (state, { payload }) => {      
      if (payload) state.enterpriseAssets = payload;
    },
    [getEnterpriseAssets.rejected]: () => {},

    // GET USERS TOPUP
    [getUserLists.pending]: () => {},
    [getUserLists.fulfilled]: (state, { payload }) => {
      if (payload) state.users_topup = payload;
    },
    [getUserLists.rejected]: () => {},

    // GET GIFT
    [getGift.pending]: () => {},
    [getGift.fulfilled]: (state, { payload }) => {
      if (payload?.data) state.gift = payload?.data;
    },
    [getGift.rejected]: () => {},

    // POST GIFT
    [postGift.pending]: (state) => {
      state.loading = true;
      state.isGiftSuccess = false;
    },
    [postGift.fulfilled]: (state, { payload }) => {
      if (payload.msg === "success") {
        state.isGiftSuccess = true;
        state.loading = false;
        state.error = false;
      } else {
        state.loading = false;
        state.error = true;
      }
    },
    [postGift.rejected]: (state) => {
      state.loading = false;
    },

    // POST ENTERPRISE GIFT START ONE
    [postEnterpriseGiftStartOne.pending]: (state) => {
      state.loading = true;
      state.isGiftPointsSuccess = false;
    },
    [postEnterpriseGiftStartOne.fulfilled]: (state, { payload }) => {
      if (payload.msg === "success") {
        state.isGiftPointsSuccess = true;
        state.loading = false;
        state.error = false;
      } else {
        state.loading = false;
        state.error = true;
      }
    },
    [postEnterpriseGiftStartOne.rejected]: (state) => {
      state.loading = false;
    },

    // GET SUBSCRIPTION HISTORY
    [getSubscriptionHistory.pending]: (state) => {
      state.loading = true;
    },
    [getSubscriptionHistory.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.history = payload;
        state.loading = false;
      }
    },
    [getSubscriptionHistory.rejected]: (state) => {
      state.loading = false;
    },

    // GET ENTERPRISE ORDERS
    [getEnterpriseOrders.pending]: (state) => {
      state.loading = true;
    },
    [getEnterpriseOrders.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.history = payload;
        state.loading = false;
      }
    },
    [getEnterpriseOrders.rejected]: (state) => {
      state.loading = false;
    },

    // GET PERSONAL LISTS
    [getPersonalLists.pending]: (state) => {
      state.loading = true;
    },
    [getPersonalLists.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.lists = payload;
        state.loading = false;
      } else {
        state.loading = false;
        state.permission_error = true;
      }
    },
    [getPersonalLists.rejected]: (state) => {
      state.loading = false;
    },

    // GET ENTERPRISE COMPANY
    [getEnterpriseCompany.pending]: (state) => {
      state.loading = true;
    },
    [getEnterpriseCompany.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.business = payload;
        state.loading = false;
        state.permission_error = false;
      } else {
        state.loading = false;
        state.permission_error = true;
      }
    },
    [getEnterpriseCompany.rejected]: (state) => {
      state.loading = false;
    },

    [getEnterpriseCompanyList.pending]: (state) => {
      state.loading = true;
    },
    [getEnterpriseCompanyList.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.business = payload;
        state.loading = false;
        state.permission_error = false;
      } else {
        state.loading = false;
        state.permission_error = true;
      }
    },
    [getEnterpriseCompanyList.rejected]: (state) => {
      state.loading = false;
    },

    // ACTIVATE USER START ONE
    [activateUserStartOne.pending]: (state) => {
      state.loading = true;
      state.isSuccess = false;
    },
    [activateUserStartOne.fulfilled]: (state, { payload }) => {
      if (payload.msg === "success") {
        state.isSuccess = true;
        state.loading = false;
        state.error = false;
      } else {
        state.loading = false;
        state.error = true;
      }
    },
    [activateUserStartOne.rejected]: (state) => {
      state.loading = false;
    },

    // ENABLE START ONE PLAN
    [enableStartOnePlan.pending]: (state) => {
      state.loading = true;
    },
    [enableStartOnePlan.fulfilled]: (state) => {
      state.loading = false;
    },
    [enableStartOnePlan.rejected]: (state) => {
      state.loading = false;
    },

    // ACTIVATE ENTERPRISE COMPANY
    [activateEnterpriseCompany.pending]: (state) => {
      state.loading = true;
      state.isSuccess = false;
    },
    [activateEnterpriseCompany.fulfilled]: (state, { payload }) => {
      if (payload.msg === "success") {
        state.isSuccess = true;
        state.loading = false;
        state.error = false;
      } else {
        state.loading = false;
        state.error = true;
      }
    },
    [activateEnterpriseCompany.rejected]: (state) => {
      state.loading = false;
    },
    // POST MANUAL INVOICE
    [postManualInvoice.pending]: (state) => {
      state.loading = true;
    },
    [postManualInvoice.fulfilled]: (state, { payload }) => {
      state.loading = false;
    },
    [postManualInvoice.rejected]: (state) => {
      state.loading = false;
    },
    // DELETE MANUAL INVOICE
    [deleteManualInvoice.pending]: (state) => {
      state.loading = true;
    },
    [deleteManualInvoice.fulfilled]: (state, { payload }) => {
      state.loading = false;
    },
    [deleteManualInvoice.rejected]: (state) => {
      state.loading = false;
    },
    // GET MANUAL INVOICE
    [getManualInvoice.pending]: (state) => {
      state.loading = true;
    },
    [getManualInvoice.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.invoice_detail = payload;
    },
    [getManualInvoice.rejected]: (state) => {
      state.loading = false;
    },
    // open Trial Plan
    [openTrialPlan.pending]: (state) => {
      state.loading = true;
    },
    [openTrialPlan.fulfilled]: (state, { payload }) => {
      if (payload.msg === "success") {
        state.loading = false;
      }
    },
    [openTrialPlan.rejected]: (state) => {
      state.loading = false;
    },

    //remove company
    [removeCompany.pending]: (state) => {
      state.loading = true;
    },
    [removeCompany.fulfilled]: (state, { payload }) => {
      if (payload.msg === "success") {
        state.loading = false;
      }
    },
    [removeCompany.rejected]: (state) => {
      state.loading = false;
    },
  },
});

export default enterpriseCompanySlice.reducer;
export const selectEnterpriseCompany = (state) => state.enterpriseCompany;
export const { removeSuccess } = enterpriseCompanySlice.actions;
